import React, { useMemo } from 'react';

import { Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import yaml from 'js-yaml';

import styles from '../FileViewer.module.css';

const { Text } = Typography;

type TensorSpec = {
  name: string;
  type: string;
  'tensor-spec'?: {
    dtype: string;
    shape: number[];
  };
};

type SchemaRow = {
  key: string;
  name: string;
  dtype: string;
  shape: string;
};

type ModelSchemaViewerProps = {
  yamlContent: string;
};

type MlModelData = {
  signature?: {
    inputs?: TensorSpec[];
    outputs?: TensorSpec[];
  };
};

const parseTensorSpecs = (specs: TensorSpec[], prefix: string): SchemaRow[] => {
  return specs.map((spec, index) => {
    const tensorSpec = spec['tensor-spec'];
    return {
      key: `${prefix}-${index}`,
      name: spec.name || `${prefix} ${index + 1}`,
      dtype: tensorSpec?.dtype || spec.type || '-',
      shape: tensorSpec?.shape && tensorSpec.shape.length > 0 ? JSON.stringify(tensorSpec.shape) : '-',
    };
  });
};

export const ModelSchemaViewer: React.FC<ModelSchemaViewerProps> = ({ yamlContent }) => {
  const schemaSections = useMemo(() => {
    try {
      const mlmodelData = yaml.load(yamlContent) as MlModelData;
      if (!mlmodelData?.signature) return [];

      const { inputs, outputs } = mlmodelData.signature;
      const sections = [];

      if (inputs && Array.isArray(inputs)) {
        sections.push({
          key: 'inputs',
          title: 'Inputs',
          data: parseTensorSpecs(inputs, 'Input'),
        });
      }

      if (outputs && Array.isArray(outputs)) {
        sections.push({
          key: 'outputs',
          title: 'Outputs',
          data: parseTensorSpecs(outputs, 'Output'),
        });
      }

      return sections;
    } catch (e) {
      console.error('Failed to parse MLmodel schema:', e);
      return [];
    }
  }, [yamlContent]);

  const columns: ColumnsType<SchemaRow> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name: string) => <Text strong>{name}</Text>,
    },
    {
      title: 'Type',
      dataIndex: 'dtype',
      key: 'dtype',
    },
    {
      title: 'Shape',
      dataIndex: 'shape',
      key: 'shape',
    },
  ];

  if (schemaSections.length === 0) {
    return <div className={styles.unsupported}>표시할 모델 스키마 정보가 없습니다.</div>;
  }

  return (
    <div className={styles.scrollableContent} style={{ padding: '16px' }}>
      {schemaSections.map((section) => (
        <div key={section.key} style={{ marginBottom: '24px' }}>
          <Typography.Title level={5}>{section.title}</Typography.Title>
          <Table columns={columns} dataSource={section.data} rowKey="key" pagination={false} size="small" bordered />
        </div>
      ))}
    </div>
  );
};
